import clsx from 'clsx'
import React, { ChangeEvent } from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import CloudUploadIcon from '@mui/icons-material/CloudUpload'

import { Divider, FormControl, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { BorderoEnvio } from '../../bordero.types'
import BorderoFormScope from '../bordero_form.scopes'

const LOG = Logger.get('BORDERO-FORM-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1
})

type BorderoFormViewProps = {
    className?: string
    scope: BorderoFormScope
}
class BorderoFormViewClass implements FCClassContext<BorderoFormViewProps> {
    // :: Fields
    scope!: BorderoFormScope
    sytles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        this.scope.onFileChange(file).catch(LOG.caught)
    }

    readonly emitFormDataChange = (dadosAlterados: Partial<BorderoEnvio>) => {
        this.scope.onFormDataChange(dadosAlterados).catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: BorderoFormViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: BorderoFormViewProps) {
        this.scope = bindUpdate(React, scope)

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10, width: 500, height: 600 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <FormControl>
                            <InputLabel id="grupo-select-label">Grupo</InputLabel>
                            <Select
                                sx={{ minWidth: 300 }}
                                labelId="grupo-select-label"
                                id="grupo-select"
                                label="Grupo"
                                full-width
                                onChange={(evt) =>
                                    this.emitFormDataChange({
                                        grupo_id: Number(evt.target.value)
                                    })
                                }
                            >
                                {this.scope.gruposAtivos.map((gp) => (
                                    <MenuItem key={gp.id} value={gp.id}>
                                        {gp.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <TextField
                            className={classes.cnpjInput}
                            value={this.scope.formData.cedente_cnpj}
                            onChange={(evt) => this.emitFormDataChange({ cedente_cnpj: evt.target.value })}
                            label="Cedente CNPJ"
                            variant="standard"
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <FormControl>
                            <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                tabIndex={-1}
                                startIcon={<CloudUploadIcon />}
                            >
                                Upload arquivo .REM
                                <VisuallyHiddenInput type="file" onChange={this.emitFileChange} multiple />
                            </Button>

                            {this.scope.formData.arquivo_nome && (
                                <div>
                                    <p>
                                        <strong>Nome do arquivo:</strong> {this.scope.formData.arquivo_nome}
                                    </p>
                                </div>
                            )}
                        </FormControl>
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginTop: '10px'
                        }}
                    >
                        <Button variant="outlined" onClick={this.emitOnSave}>
                            Salvar
                        </Button>
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Voltar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const BorderoFormView = classToFComponent(BorderoFormViewClass, React)
export default BorderoFormView
